import React from "react";
import { Contract, ContractStatus, League, LeaguePhase, Player, PlayerPoachInfo, PlayerPositionEnum, RosterPositionEnum, TagType, Team, TeamPlayerInfo, TradeProposalPlayer, TradeProposalPlayerActionEnum } from "../../sdk/model";
import FranchiseTagButton from "../body/team/FranchiseTagButton";
import DropPlayerButton from '../body/team/DropPlayerButton';
import SetAutoSubButton from '../body/team/SetAutoSubButton';
import RookieOptionTagButton from '../body/team/RookieOptionTagButton';
import RemoveTagButton from '../body/team/RemoveTagButton';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FinalizeTagButton from '../body/team/FinalizeTagButton';
import { getNumberOrdinal } from "./DraftPickDisplay";
import { getPlayerCurrentScoreFromStats } from "./ScoreService";
import { getCurrentSalaryAsNumber, getFinalSeasonOfContract, getFinalSeasonOfContractFromContract } from './SalaryDisplay';
import { MINIMUM_ONE_YEAR_SALARY } from "./ContractUtils";
import { positionOrderNoBench } from "./PositionOrder";
import { contractApi, teamApi } from "../../adapters/APIExporter";
import { MIN_WEEKS_ON_IR } from "./TeamUtils";
import { isPlayerLocked } from "./PlayerDataUtil";

export enum SwapOption {
  SWAP, 
  MOVE_TO, 
  MOVE_FROM, 
  NONE,
  SELECTED
}

export const ACTIVE_LEAGUE_PHASES = new Set([LeaguePhase.RegularSeason, LeaguePhase.Playoffs]);

export const NON_ACTIVE_ROSTER_SPOTS = new Set([RosterPositionEnum.Bn, RosterPositionEnum.Ir, RosterPositionEnum.Ps]);

const ROSTER_POSITIONS_FOR_CONFIRMATION : Set<string> = new Set([RosterPositionEnum.Ir, RosterPositionEnum.Ps]);


export interface PlayerRowData {
  id: string;
  contractTypes: any; // TODO: Replace 'any' with proper type from getContractTypeInfo
  position: RosterPositionEnum;
  player: Player | undefined;
  projection: number | undefined;
  previousYearSalary: number;
  currentYearSalary: number;
  nextYearSalary: number;
  finalYearOfContract: number | undefined;
  teamPlayer: TeamPlayerInfo | undefined;
  poachOffer: boolean;
  selectable: boolean | undefined;
  autoSwapPlayer: string | undefined;
}


export const SWAP_BUTTON_MAP = new Map([
  [SwapOption.SWAP, <SwapHorizIcon />],
  [SwapOption.MOVE_TO, <ArrowForwardIcon />],
  [SwapOption.MOVE_FROM, <ArrowBackIcon />],
  [SwapOption.NONE, <></>],
  [SwapOption.SELECTED, <CheckCircleIcon color="primary" sx={{ ml: 1 }} />  ]
]);


// I wonder if there is a better way to do this than hard coding
const ROSTER_SPOTS_FOR_POSITION : Map<PlayerPositionEnum, RosterPositionEnum[]> = new Map([
  [PlayerPositionEnum.Qb, [RosterPositionEnum.Qb, RosterPositionEnum.Sflex, RosterPositionEnum.Bn, RosterPositionEnum.Ir, RosterPositionEnum.Ps]],
  [PlayerPositionEnum.Rb, [RosterPositionEnum.Rb, RosterPositionEnum.Sflex, RosterPositionEnum.Flex, RosterPositionEnum.Bn, RosterPositionEnum.Ir, RosterPositionEnum.Ps]],
  [PlayerPositionEnum.Wr, [RosterPositionEnum.Wr, RosterPositionEnum.Sflex, RosterPositionEnum.Flex, RosterPositionEnum.Bn, RosterPositionEnum.Ir, RosterPositionEnum.Ps]],
  [PlayerPositionEnum.Te, [RosterPositionEnum.Te, RosterPositionEnum.Sflex, RosterPositionEnum.Flex, RosterPositionEnum.Bn, RosterPositionEnum.Ir, RosterPositionEnum.Ps]],
]);



export function fillBenchData(benchData : Array<any>, starterData : Array<any>, rosterSettings : Map<string | undefined, number | undefined>, playerPoachInfo : PlayerPoachInfo[], currentSeasonYear : number, playerMap : Map<string | undefined, Player | undefined>, league : League) {
  // console.log('fillBenchData called with benchData length:', benchData?.length, 'starterData length:', starterData?.length);
  let emptySpotCount = (rosterSettings!.get(RosterPositionEnum.Bn) || 0) - benchData.length;
  if (emptySpotCount > 0) {
    [...Array(emptySpotCount).keys()].map(index => index + benchData.length).forEach(index => benchData.push(rosterSpotToRowData(RosterPositionEnum.Bn, undefined, index, playerPoachInfo, currentSeasonYear, playerMap, league)));
  }
}

  
  export function rosterSpotToRowData(
    rosterPosition: RosterPositionEnum,
    playerInfo: TeamPlayerInfo | undefined,
    playerPositionIndex: number,
    playerPoachInfo: PlayerPoachInfo[],
    currentSeasonYear: number,
    playerMap: Map<string | undefined, Player | undefined>,
    league: League
  ): PlayerRowData {
    const player = playerInfo ? playerMap.get(playerInfo.playerId) || undefined : undefined;
    let projection : number | undefined = undefined;
    if (player) {
      projection = getPlayerCurrentScoreFromStats(player, league?.currentSeason?.scoringSettings!, undefined).projectedScore;
    }
    return {
      id: rosterPosition + '-' + playerPositionIndex,
      contractTypes: getContractTypeInfo(playerInfo),
      position: rosterPosition,
      player: player,
      projection: projection, 
      previousYearSalary: playerInfo ? Number(getCurrentSalaryAsNumber(playerInfo, currentSeasonYear - 1) || 0) : 0,
      currentYearSalary: playerInfo ? Number(getCurrentSalaryAsNumber(playerInfo, currentSeasonYear) || 0) : 0,
      nextYearSalary: playerInfo ? Number(getCurrentSalaryAsNumber(playerInfo, currentSeasonYear + 1) || 0) : 0,
      finalYearOfContract: playerInfo ? getFinalSeasonOfContract(playerInfo) : undefined,
      teamPlayer: playerInfo,
      poachOffer: !!(playerInfo && playerPoachInfo?.some(playerPoach => playerInfo.playerId === playerPoach.playerId)),
      selectable: true,
      autoSwapPlayer: playerInfo?.autoSwapPlayerId
    }
  }

export const TAG_TYPE_INFO_MAPPER : Map<TagType, Function> = new Map([
  [TagType.Franchise, (contract : Contract, draft : boolean) => {
    let consecutiveTagNumber = contract.consecutiveTagNumber!;
    if (draft) {
      consecutiveTagNumber++;
    }
    return {
      display: (draft ? "*" : "") + 'F' + consecutiveTagNumber,
      details: getNumberOrdinal(consecutiveTagNumber!) + " Consecutive Franchise Tag" + (draft ? " - NOT FINALIZED" : "")
    };
  }],
  [TagType.RookieOption, (contract : Contract, draft : boolean) => {
    return {
      display: (draft ? "*" : "") + "5",
      details: "5th-Year Rookie Option" + (draft ? " - NOT FINALIZED" : "")
    };
  }]
]);

export interface ContractType {
  display: string,
  details: string
}

export function getContractTypeInfo(playerInfo : TeamPlayerInfo | undefined) {
  // console.log('getContractTypeInfo called for player:', playerInfo?.playerId);
  const info : ContractType[] = []
  if (!playerInfo) {
    return info;
  }
  if (playerInfo.contract?.rookieContract) {
    info.push({
      display: 'R' + (playerInfo.contract?.rookieDraftRound || 1),
      details: getNumberOrdinal(playerInfo.contract?.rookieDraftRound || 1) + " Round Rookie Pick"
    });
  }
  if (playerInfo.contract?.markedTagForNextContract) {
    info.push(TAG_TYPE_INFO_MAPPER.get(playerInfo.contract?.markedTagForNextContract)!(playerInfo.contract!, true))
  }
  if (playerInfo.contract?.tag) {
    info.push(TAG_TYPE_INFO_MAPPER.get(playerInfo.contract?.tag)!(playerInfo.contract!, false))
  }
  return info;
}

/**
 * Validates player exist and can be moved to the specified position
 * @param player 
 * @param position 
 * @returns 
 */
export function canMove(player, position) {
  return player && ROSTER_SPOTS_FOR_POSITION.get(player.position)!.includes(position);
}

interface getActionButtonsProps {
  selectedPlayer: Player;
  selectedPlayerInfo: TeamPlayerInfo;
  league: League;
  team: Team;
  currentSeasonYear: number;
  handleActionResponse: Function;
  setRoster: Function;
  selectedPosition: string | undefined,
  setSelectedPosition: Function;
  commish: boolean;
  playerMap: Map<string | undefined, Player | undefined>
}


export function getActionButtons(props: getActionButtonsProps) {
  // console.log('getActionButtons called for player:', props.selectedPlayer?.id);
  const selectedPlayer = props.selectedPlayer;
  const selectedPlayerInfo = props.selectedPlayerInfo;
  const league = props.league;
  const leagueId = props.league.id || "";
  const handleActionResponse = props.handleActionResponse;
  const setRoster = props.setRoster;
  const selectedPosition = props.selectedPosition;
  const setSelectedPosition = props.setSelectedPosition;
  const playerMap = props.playerMap;

  if(props.selectedPlayer) {
    if (ContractStatus.Expired === props.selectedPlayerInfo?.contract?.status) {
      if (props.team.currentSeason?.franchiseTagsUsed! < 1) {
        return <FranchiseTagButton leagueId={leagueId} player={selectedPlayer!} contract={selectedPlayerInfo?.contract!} currentSeasonYear={props.currentSeasonYear} team={props.team} callback={handleActionResponse} />
      }
      if (selectedPlayerInfo?.contract?.markedTagForNextContract) {
        return <React.Fragment>
          <RemoveTagButton league={league!} player={selectedPlayer!} tagType={TagType.Franchise} callback={handleActionResponse} />
          <FinalizeTagButton league={league!} player={selectedPlayer!} tagType={TagType.Franchise} callback={handleActionResponse} />
        </React.Fragment>
      }
      return undefined;
    }
    return <React.Fragment>
      {league?.phase === LeaguePhase.Offseason && getFinalSeasonOfContractFromContract(selectedPlayerInfo?.contract!) === league?.currentSeasonYear && selectedPlayerInfo?.contract?.rookieDraftRound === 1 &&
        (selectedPlayerInfo?.contract?.markedTagForNextContract ? 
          <RemoveTagButton league={league!} player={selectedPlayer!} tagType={TagType.RookieOption} callback={handleActionResponse} /> :
          <RookieOptionTagButton leagueId={leagueId} player={selectedPlayer!} contract={selectedPlayerInfo?.contract!} currentSeasonYear={props.currentSeasonYear} team={props.team} callback={handleActionResponse} />
        )
      }
      <DropPlayerButton leagueId={leagueId} player={selectedPlayer!} contract={selectedPlayerInfo?.contract!} currentSeasonYear={props.currentSeasonYear} team={props.team} commish={props.commish}
        callback={newRoster => {
          setRoster(newRoster);
          setSelectedPosition(undefined);
        }} 
      />
      {ACTIVE_LEAGUE_PHASES.has(league?.phase) && !!selectedPosition && !NON_ACTIVE_ROSTER_SPOTS.has(selectedPosition.substring(0, 2)) && 
          <SetAutoSubButton league={league} player={selectedPlayer!} contract={selectedPlayerInfo?.contract!} team={props.team}
            position={selectedPosition} currentSeasonYear={props.currentSeasonYear} playerMap={playerMap} callback={newRoster => {
            setRoster(newRoster);
            setSelectedPosition(undefined)
          }} />
      }
    </React.Fragment>
  }
  return undefined;
}


export function swapSpotsAfterConfirm(position1Info, position2Info, selectedPlayerInfo, otherSelectedPlayerInfo, team, bid, setRoster, setSelectedPosition, setOtherSelectedPosition, setErrorsAndFocus, setProcessing, commish) {
  // console.log('swapSpotsAfterConfirm called for positions:', position1Info, position2Info);
  setProcessing(true);
  getSwapEndpoint(position1Info, position2Info, selectedPlayerInfo, otherSelectedPlayerInfo, team,
    bid, commish
  ).then(response => {
    if (response.data) {
      setRoster(response.data);
    }
    setSelectedPosition(undefined);
    setOtherSelectedPosition(undefined);
  }).catch(error => {
    let errors = [error.response.data.message];
    setErrorsAndFocus(errors);
    setOtherSelectedPosition(undefined);
  }).finally(() => setProcessing(false));
}

function getSwapEndpoint(position1Info, position2Info, selectedPlayerInfo, otherSelectedPlayerInfo, team, bid, commish) {
  // console.log('getSwapEndpoint called for positions:', position1Info, position2Info);
  if (position1Info[0] === RosterPositionEnum.Ps || position2Info[1] === RosterPositionEnum.Ps) {
    const selectedPlayer = selectedPlayerInfo || otherSelectedPlayerInfo;
    return contractApi.activateContract(selectedPlayer?.contract?.id, commish, bid)
  }
  const payload = {
    team1Position: position1Info[0],
    team1Index: position1Info[1],
    team2Position: position2Info[0],
    team2Index: position2Info[1]
  }
  return teamApi.swapRosterPositions(team.id, payload, commish)
}

export function getFilteredBenchData(benchData : PlayerRowData[], selectedPosition : string | undefined, 
  selectedPlayer : Player | undefined,
  roster : any, playerMap : Map<string | undefined, Player | undefined>,
  playerPoachInfo : PlayerPoachInfo[], currentSeasonYear : number, league: League) {
 if (selectedPosition) {
    const selectedPositionInfo = selectedPosition.split('-');
    // Filters the bench data to only include rows that can be swapped with the selected position
    benchData = benchData.filter(benchRow => {
      // Checks if the selected position is the same as the current row
      if (selectedPosition === benchRow.id) {
        return true;
      }
      const position2Info = benchRow.id.split('-');
      // Checks if the selected position can be swapped with the current row
      return new Set([SwapOption.SWAP, SwapOption.MOVE_FROM]).has(getSwapOption(selectedPositionInfo, position2Info, roster!, playerMap));
    })
    const selectedPos = selectedPositionInfo[0];
    if (selectedPlayer && selectedPos !== RosterPositionEnum.Bn) {
      benchData.push(rosterSpotToRowData(RosterPositionEnum.Bn, undefined, roster![RosterPositionEnum.Bn].length, playerPoachInfo, currentSeasonYear, playerMap, league));
    }
  }
  if (benchData.length > 0) {
    benchData.sort((playerInfo1, playerInfo2) => {
      const pos1 = playerInfo1.player?.position || RosterPositionEnum.Bn;
      const pos2 = playerInfo2.player?.position || RosterPositionEnum.Bn;
      return positionOrderNoBench.indexOf(pos1) - positionOrderNoBench.indexOf(pos2);
    });
  }

  return benchData;
}


export function getSwapOption(selectedPositionInfo : string[], otherPositionInfo : string[], roster : any, playerMap : Map<string | undefined, Player | undefined>) {
  const position1 = selectedPositionInfo[0];

  if(selectedPositionInfo.join('-') === otherPositionInfo.join('-')){
    return SwapOption.SELECTED;
  }

  if (position1 === RosterPositionEnum.Ps) {
    if (otherPositionInfo[0] === RosterPositionEnum.Bn && roster[RosterPositionEnum.Bn].length === Number(otherPositionInfo[1])) {
      return SwapOption.MOVE_TO;
    }
    return SwapOption.NONE;
  }
  const player1Index = selectedPositionInfo[1];
  const player1Info = roster[position1][player1Index];
  const player1 = player1Info ? playerMap.get(player1Info.playerId)! : undefined;
  const position2 = otherPositionInfo[0];
  if (position2 === RosterPositionEnum.Ps) {
    return SwapOption.NONE;
  }
  const player2Index = otherPositionInfo[1];
  const player2Info = roster[position2][player2Index];
  const player2 = player2Info ? playerMap.get(player2Info.playerId)! : undefined;


  // If player 1 is in ir and player 2 is not empty show none option (or position 2 is an ir spot)
  if(position1 === RosterPositionEnum.Ir && (player2 || position2 === RosterPositionEnum.Ir)){
    return SwapOption.NONE;
  }
  // If both players are empty bench or ir spots show none option (moving from bench to bench doesn't make sense)
  if (position1 === RosterPositionEnum.Bn && position2 === RosterPositionEnum.Bn) {
    return SwapOption.NONE;
  }
  // If both players can swap position show swap option
  if (canMove(player1, position2) && canMove(player2, position1)) {
    return SwapOption.SWAP;
  }
  // If player 1 is an empty bench or ir spot show move from option
  else if(!player1 && canMove(player2, position1)) {
    return SwapOption.MOVE_FROM;
  } 
  // If player 2 is an empty bench or ir spot show move to option
  else if(!player2 && canMove(player1, position2)) {
    return SwapOption.MOVE_TO;
  }
  return SwapOption.NONE;
}

  
  export function getPlayerInfo(rosterPosition : string | undefined, team : Team, roster : any) {
    // console.log('getPlayerInfo called for rosterPosition:', rosterPosition);
    if (!rosterPosition) {
      return undefined;
    }
    const positionInfo = rosterPosition?.split('-')!;
    const position = positionInfo[0];
    const playerIndex = positionInfo[1];
    if (position === RosterPositionEnum.Ps) {
      return team.practiceSquad![playerIndex];
    }
    return roster![position][playerIndex];
  }

  export function getAction(positionInfo, playerId, playerPoachInfo) {
    // console.log('getAction called for position:', positionInfo, 'playerId:', playerId);
      if (RosterPositionEnum.Ir === positionInfo) {
        return TradeProposalPlayerActionEnum.ReturnFromIr;
      } else if (RosterPositionEnum.Ps === positionInfo) {
        // setBid(playerPoachInfo?.find(playerPoach => playerId === playerPoach.playerId)?.topOffer || MINIMUM_ONE_YEAR_SALARY);
        return TradeProposalPlayerActionEnum.Activate
      }
      return TradeProposalPlayerActionEnum.MoveToIr;
    }

  export function getBid(playerPoachInfo : PlayerPoachInfo[], playerId : string | undefined) {
    if (!playerPoachInfo) {
      return MINIMUM_ONE_YEAR_SALARY;
    }
    return playerPoachInfo?.find(playerPoach => playerId === playerPoach.playerId)?.topOffer || MINIMUM_ONE_YEAR_SALARY;
  }

  export function getStarterData (
    roster : any,
    playerPoachInfo: PlayerPoachInfo[],
    currentSeasonYear: number,
    playerMap: Map<string | undefined, Player | undefined>,
    league: League | undefined) : PlayerRowData[] {
    return positionOrderNoBench
    .filter(rosterPosition => RosterPositionEnum.Bn !== rosterPosition && roster![rosterPosition])
    .flatMap(rosterPosition => 
      roster![rosterPosition].map((playerInfo, playerPositionIndex) => rosterSpotToRowData(rosterPosition, playerInfo, playerPositionIndex, playerPoachInfo, currentSeasonYear, playerMap, league))
    );
  }


  export function getBenchData (
    roster : any,
    playerPoachInfo: PlayerPoachInfo[],
    starterData : PlayerRowData[],
    rosterSettings : Map<string | undefined, number | undefined>,
    currentSeasonYear: number,
    playerMap: Map<string | undefined, Player | undefined>,
    league: League | undefined) : PlayerRowData[] {
      let benchData = [...roster![RosterPositionEnum.Bn]]
        .map((playerInfo, playerPositionIndex) => rosterSpotToRowData(RosterPositionEnum.Bn, playerInfo, playerPositionIndex, playerPoachInfo, currentSeasonYear, playerMap, league!))
        .sort((playerInfo1, playerInfo2) => positionOrderNoBench.indexOf(playerInfo1.player.position) - positionOrderNoBench.indexOf(playerInfo2.player.position));
      fillBenchData(benchData, starterData, rosterSettings!, playerPoachInfo, currentSeasonYear, playerMap, league);
      
      return benchData;
  }

  export function getIrData (
    roster : any,
    playerPoachInfo: PlayerPoachInfo[],
    currentSeasonYear: number,
    playerMap: Map<string | undefined, Player | undefined>,
    league: League | undefined) : PlayerRowData[] {
      const ir = roster![RosterPositionEnum.Ir];
      return ir ? ir
        .map((playerInfo, playerPositionIndex) => rosterSpotToRowData(RosterPositionEnum.Ir, playerInfo, playerPositionIndex, playerPoachInfo, currentSeasonYear, playerMap, league!)) 
        : []
  }

  export function getPlayersForTrade(
    selectedPlayer : Player | undefined,
    otherSelectedPlayer : Player | undefined,
    selectedPosition : string | undefined,
    otherSelectedPosition : string | undefined,
    team : Team,
    bid: number,
    playerPoachInfo : PlayerPoachInfo[]) : TradeProposalPlayer[] {
      
      const players : TradeProposalPlayer[] = []; 
      if (selectedPlayer) {
        const positionInfo = selectedPosition?.split('-')![0];
        players.push({
          playerId: selectedPlayer?.id,
          fromTeamId: team.id,
          toTeamId: team.id,
          action: getAction(positionInfo, selectedPlayer?.id, playerPoachInfo),
          newBidValue: bid
        });
      }
      
      if (otherSelectedPlayer) {
        const positionInfo = otherSelectedPosition?.split('-')![0];
        players.push({
          playerId: otherSelectedPlayer?.id,
          fromTeamId: team.id,
          toTeamId: team.id,
          action: getAction(positionInfo, otherSelectedPlayer?.id, playerPoachInfo),
          newBidValue: bid
        });
    } 
    return players;
  }

  export function playerLocked(player : Player | undefined, teamPlayer : TeamPlayerInfo | undefined, position : string, league : League | undefined) {
    return (ACTIVE_LEAGUE_PHASES.has(league?.phase) && isPlayerLocked(player)) || 
    (position === RosterPositionEnum.Ir && teamPlayer && teamPlayer?.weeksSpentOnIr! < MIN_WEEKS_ON_IR);
  }
  
  export function getPsData (
    team: Team,
    playerPoachInfo: PlayerPoachInfo[],
    currentSeasonYear: number,
    playerMap: Map<string | undefined, Player | undefined>,
    league: League | undefined) : PlayerRowData[] {
      return team.practiceSquad?.map((playerInfo, playerPositionIndex) => rosterSpotToRowData(RosterPositionEnum.Ps, playerInfo, playerPositionIndex, playerPoachInfo, currentSeasonYear, playerMap, league)) || []
  }
  
  export function isRequireConfirmation(positionInfo : string[]) {
    return ROSTER_POSITIONS_FOR_CONFIRMATION.has(positionInfo[0]);
  }